import { Box, Button, Grid, Typography } from '@mui/material';
import { CompetitionOnboardingStatus } from 'common/@types/recommendations';
import { Student } from 'common/hooks/useStudentSelection';
import { useState } from 'react';
import { getGradeObject } from '../utils/grade-mappings';
import LoadingScreen from '@common/components/LoadingScreen';

export type GradeOption = {
  label: string;
  value: number;
};

type GradeOptionsProp = {
  gradeOptions: GradeOption[];
  selectedGrade?: number;
  setSelectedGrade: (grade: number) => void;
};

const earningsIndexMap: { [key: number]: string } = {
  0: ' (winner gets $200)',
  1: ' (winner gets $400)',
  2: ' (winner gets $800)',
  3: ' (winner gets $1600)',
  4: ' (winner gets $3200)',
};

export function GradeOptions({ gradeOptions, selectedGrade, setSelectedGrade }: GradeOptionsProp) {
  // Calculate grid item size based on the number of items
  const gridSize = Math.floor(12 / gradeOptions.length);

  return (
    <Grid container justifyContent="center" display="flex" spacing={2}>
      {gradeOptions.map((gradeOption, index) => (
        <Grid key={gradeOption.value} item display="flex" justifyContent="center" xs={gridSize}>
          <Button
            variant={gradeOption.value === selectedGrade ? 'contained' : 'outlined'}
            color="primary"
            sx={{
              padding: 2,
              color: '#000',
              borderColor: '#525252',
              fontWeight: 700,
              boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
            }}
            onClick={() => setSelectedGrade(gradeOption.value)}
          >
            {`${gradeOption.label}${earningsIndexMap[index]}`}
          </Button>
        </Grid>
      ))}
    </Grid>
  );
}

type PickGradeProps = {
  competitionStatus: CompetitionOnboardingStatus | undefined;
  gradeOptions: GradeOption[];
  student: Student;
  selectGrade: (grade?: number) => void;
  previousStep: () => void;
};

export function PickGrade({
  competitionStatus,
  gradeOptions,
  student,
  selectGrade,
  previousStep,
}: PickGradeProps) {
  const [selectedGrade, setSelectedGrade] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  if (loading) {
    return (
      <>
        <Typography
          variant={'h4'}
          sx={{
            width: '100%',
            backgroundColor: '#fff',
            paddingTop: '40px',
            paddingLeft: '10px',
          }}
        >
          Generating your skill plan...
        </Typography>
        <LoadingScreen positionFixed={false} />
      </>
    );
  }
  if (
    !competitionStatus ||
    !competitionStatus.screener_overall_result ||
    [7, 5, 4].includes(competitionStatus.screener_overall_result)
  ) {
    return (
      <>
        <Typography variant="h3" marginBottom={4}>
          Error Retrieving your Screener Results
        </Typography>
        <Typography variant="body1" marginY={2}>
          This can happen if you marked your screener test as done but we haven't received its
          results yet. Please try to refresh this page after a few minutes to check again.
        </Typography>
        <Typography variant="body1" marginY={2}>
          If you have completed your IXL Screening Test and this issue persists, please contact
          support, using the icon on the bottom right corner of this page!
        </Typography>

        <Typography variant="body1" marginY={2}>
          If you haven't completed your IXL Screening Test, then use the button below to go back to
          the previous step to attempt the test:
        </Typography>

        <Box width="100%" display="flex" justifyContent="center" marginY={4}>
          <Button variant="outlined" sx={{ padding: 2 }} onClick={() => previousStep()}>
            Go Back to the IXL Screening Test
          </Button>
        </Box>
      </>
    );
  } else if ([6, 8].includes(competitionStatus.screener_overall_result)) {
    return (
      <>
        <Typography variant="h3" marginBottom={4}>
          Seems you have not yet finished your screener
        </Typography>
        <Typography variant="body1" marginY={2}>
          If you haven't completed your IXL Screening Test, then use the button below to go back to
          the previous step to attempt the test:
        </Typography>

        <Box width="100%" display="flex" justifyContent="center" marginY={4}>
          <Button variant="outlined" sx={{ padding: 2 }} onClick={() => previousStep()}>
            Go Back to the IXL Screening Test
          </Button>
        </Box>
        <Typography variant="body1" marginY={2}>
          If you have completed your IXL Screening Test, please try to refresh this page after a few
          minutes to check again. If this issue persists, please contact support, using the icon on
          the bottom right corner of this page!
        </Typography>
      </>
    );
  }

  // AI-GEN START - Tool: Codeium, AI: GPT-4
  const lowestGrade = getGradeObject(gradeOptions[0].value.toString());
  const highestGrade = getGradeObject(gradeOptions[gradeOptions.length - 1].value.toString());
  // AI-GEN END - Tool: Codeium, AI: GPT-4

  return (
    <>
      <Typography variant="h3" marginBottom={4}>
        Select Your Competitive Grade Level
      </Typography>
      <Typography variant="body1" marginY={2}>
        To start your SpeedRun Journey, it's crucial to select the grade level you'll compete in, as
        this decision impacts both your experience during Esports SpeedRun and potential prize
        earnings.
      </Typography>
      <Typography variant="body1" marginY={2}>
        The grade you choose will determine the difficulty of the lessons you'll master and the
        winning potential. Here are your options, ranging from {lowestGrade?.shortName} to{' '}
        {highestGrade?.shortName}:
      </Typography>

      <GradeOptions
        gradeOptions={gradeOptions}
        selectedGrade={selectedGrade}
        setSelectedGrade={setSelectedGrade}
      />

      <Typography variant="body1" marginY={2}>
        In Esports SpeedRun, picking your grade level shapes your quest. Opt for a lower grade, like
        3rd when you're in 5th, to win $200 with ease. Or, challenge yourself in your grade for
        $800, maybe even daring to tackle {highestGrade?.shortName} for up to $3,200! It's a
        thrilling balance of risk and reward.
      </Typography>
      <Typography variant="body1" marginY={2}>
        Your choice kickstarts your <strong>Esports SpeedRun</strong> adventure. Will you play it
        safe or aim high for the grand prize? Every decision carves your path to success. What's
        your strategy? Dive into Esports SpeedRun, and let's see where your courage takes you!
      </Typography>
      <Typography variant="body1" marginY={2}>
        <strong>Once you made your selection, you cannot change it later - make it count!</strong>
      </Typography>

      <Box width="100%" display="flex" justifyContent="center" marginY={4}>
        <Button
          variant="outlined"
          disabled={selectedGrade === undefined}
          sx={{ padding: 2 }}
          onClick={() => {
            selectGrade(selectedGrade);
            setLoading(true);
          }}
        >
          Select Grade Level
        </Button>
      </Box>
    </>
  );
}
